import { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControlLabel,
	Grid,
	Skeleton,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteIcon, SearchIcon } from "../../../../assets/icons";
import { AppRoutes } from "../../../../common/routesUtils";
import { col6 } from "../../../../common/utils";
import GraphMap from "../../../../components/charts/GraphMap";
import { MarkerLayer } from "../../../../components/charts/GraphMap/layers";
import ErrorMessage from "../../../../components/common/ErrorMessage";
import ForbiddenTooltip from "../../../../components/common/ForbiddenTooltip";
import { DeleteRowsDialog } from "../../../../components/common/table/TableData/TableSelectActions/components/DeleteRowsDialog";
import { View } from "../../../../components/layout/View";
import PaperCard from "../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../../config/page.types";
import { useBreadcrumbs } from "../../../../context/BreadcrumbsProvider";
import {
	ErrorResponse,
	NodeDetailsDTO,
	NodeLocationDTO,
	NodesDetailsPageItemDTO,
	NodesPageItemDTO,
	SitePayload,
} from "../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { GetOptionsTopic } from "../../../../datasources/requests/common";
import { useServerOptions } from "../../../../hooks/useOptions";
import { usePeriodFilter } from "../../../../hooks/usePreferences";
import { useReadResource, useWriteResource } from "../../../../hooks/useResource";
import { usePermissions } from "../../../../permissions/usePermissions";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../../../state/view";
import { ThemeColors } from "../../../../theme/themeValues";
import { GeneralInformation } from "./components/cards/GeneralInformation";
import { RegistrationDetails } from "./components/cards/RegistrationDetails";

// Form values used in react-hook-form
export type FormValues = {
	code: string;
	typeId: number;
	address: string;
	description: string; // location name
	zipCode: string;
	city: string;
	province: string;
	country: string;
	lat?: number | string;
	lng?: number | string;

	locked: boolean;

	area?: number;
	capacity?: number;
	temperature?: number;
	constructionYear?: number;
	valueAddedServiceId?: number | null;
	activityId?: number | null;
	buildingCertificationId?: number | null;
	sector?: string;
	computeFlag: number;
};

export type NodeInformation = {
	id: string;
	goodsFlowMeasureId?: number;
	goodsFlowConversionFactor?: number;
};

const LIMITED_GENERAL_NODE_TYPES = [9, 10, 11];
const HIDDEN_GENERAL_NODE_TYPES = [5, 6, 7];

const getValidCoordinate = (value: unknown): number | undefined => {
	if (value === "" || value === null || value === undefined) {
		return undefined;
	}

	const coordinate = Number(value);

	return Number.isFinite(coordinate) ? coordinate : undefined;
};

export const MasterDataOperationalUnitsDetail = () => {
	const { t } = useTranslation(["masterData", "common"]);

	const { formattedFrom: from, formattedTo: to } = usePeriodFilter();

	const containerRef = useRef<HTMLDivElement | null>(null);

	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();
	const { canCall } = usePermissions();

	// routing params
	const params = useParams();
	const id = params.id || "";

	// check if we are in create mode
	const isNewOperationalUnit = id === "add-unit";

	// dialogs state
	const [openRegistrationDetailsDialog, setOpenRegistrationDetailsDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	// fetch node details (edit mode only)
	const dataNode = useReadResource<undefined, NodeDetailsDTO>(
		{
			route: ApiRoutes.ResultsSitesNodeDetails,
			path: { id },
			query: { from, to },
		},
		{
			disabled: isNewOperationalUnit,
		}
	);

	// API: create new node
	const { post: saveNewNode, isLoading: saveNodeLoading } = useWriteResource<SitePayload, NodeDetailsDTO>(
		{ route: ApiRoutes.ConfigurationSitesNodes },
		{ subjectKey: "node" }
	);

	const {
		put: updateNode,
		isLoading: updateNodeLoading,
		isForbidden: updateNodeForbidden,
	} = useWriteResource<SitePayload, NodesDetailsPageItemDTO>(
		{ route: ApiRoutes.ConfigurationSitesNodesUpdate, path: { id } },
		{ subjectKey: "node" }
	);

	const {
		put: deleteNode,
		isLoading: deleteNodeLoading,
		isForbidden: deleteNodeForbidden,
	} = useWriteResource<any, ErrorResponse>({ route: ApiRoutes.ConfigurationDeleteNodes }, { subjectKey: "node" });

	// dropdown options
	const {
		data: countriesData,
		isLoading: isCountriesLoading,
		error: countriesError,
	} = useServerOptions(GetOptionsTopic.Countries);

	const {
		data: nodeTypesData,
		isLoading: isNodeTypesLoading,
		error: nodeTypesError,
	} = useServerOptions(GetOptionsTopic.NodeTypes);

	const {
		data: computeFlags,
		isLoading: isComputeFlagsLoading,
		error: computeFlagsError,
	} = useServerOptions(GetOptionsTopic.ComputeFlags);

	const {
		data: valueAddedServices,
		isLoading: isValueAddedServicesLoading,
		error: valueAddedServicesError,
	} = useServerOptions(GetOptionsTopic.ValueAddedServices);

	const {
		data: nodeActivities,
		isLoading: isNodeActivitiesLoading,
		error: nodeActivitiesError,
	} = useServerOptions(GetOptionsTopic.NodeActivities);

	const {
		data: certifications,
		isLoading: isCertificationsLoading,
		error: certificationsError,
	} = useServerOptions(GetOptionsTopic.Certifications);

	const {
		data: sectors,
		isLoading: isSectorsLoading,
		error: sectorsError,
	} = useServerOptions(GetOptionsTopic.Sectors);

	// loading states
	const registrationDetailLoading =
		isCountriesLoading || isNodeTypesLoading || dataNode.isLoading || saveNodeLoading || updateNodeLoading;

	const generalInformationLoading =
		isComputeFlagsLoading ||
		isValueAddedServicesLoading ||
		isNodeActivitiesLoading ||
		isCertificationsLoading ||
		isSectorsLoading ||
		dataNode.isLoading ||
		saveNodeLoading ||
		updateNodeLoading;

	// global error
	const error =
		countriesError ||
		nodeTypesError ||
		computeFlagsError ||
		valueAddedServicesError ||
		nodeActivitiesError ||
		certificationsError ||
		sectorsError;

	// dialog handlers
	const handleOpenRegistrationDialog = () => setOpenRegistrationDetailsDialog(true);
	const handleCloseRegistrationDialog = () => setOpenRegistrationDetailsDialog(false);

	const handleOpenDeleteDialog = () => setOpenDeleteDialog(true);
	const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

	// form setup
	const form = useForm<FormValues>({
		mode: "onChange",
		defaultValues: useMemo(
			() => ({
				code: "",
				typeId: undefined,
				address: "",
				description: "",
				zipCode: "",
				city: "",
				province: "",
				country: "",
				lat: undefined,
				lng: undefined,
				locked: false,
				area: undefined,
				capacity: undefined,
				temperature: undefined,
				constructionYear: undefined,
				valueAddedServiceId: undefined,
				activityId: undefined,
				buildingCertificationId: undefined,
				sector: "",
				computeFlag: undefined,
			}),
			[]
		),
	});

	// check if form has been modified
	const { isDirty, isValid } = form.formState;

	// watch locked flag
	const locked = form.watch("locked");

	// submit handler (create / update)
	const onSubmit = async (data: FormValues) => {
		try {
			const lat = getValidCoordinate(data.lat);
			const lng = getValidCoordinate(data.lng);

			const payload: SitePayload = {
				...data,
				lat,
				lng,
			} as SitePayload;

			// create new node (POST)
			if (isNewOperationalUnit) {
				const createdNewNode = await saveNewNode(payload);

				const newId = createdNewNode.id;
				const newCode = createdNewNode.code;

				// update current breadcrumb
				visitPage(PageType.MasterDataOperationalUnitsDetail, false, newCode, String(newId));

				// update url, from current add-unit to created ID
				navigate(AppRoutes.MasterDataOperationalUnitsDetail.replace(":id", String(newId)), {
					replace: true,
				});
			} else {
				await updateNode(payload);
			}
		} catch (e) {}
	};

	// map marker state
	const latValue = form.watch("lat");
	const lngValue = form.watch("lng");

	// Convert form values only when coordinates are valid
	const lat = useMemo(() => getValidCoordinate(latValue), [latValue]);
	const lng = useMemo(() => getValidCoordinate(lngValue), [lngValue]);

	const mapPosition = useMemo<[number, number] | undefined>(() => {
		if (lat === undefined || lng === undefined) {
			return undefined;
		}

		return [lat, lng];
	}, [lat, lng]);

	// create map marker if coordinates exist
	const nodeData = useMemo<NodesPageItemDTO[] | undefined>(() => {
		if (!mapPosition) {
			return undefined;
		}

		return [
			{
				lat: mapPosition[0],
				lng: mapPosition[1],
				id: "",
				typeId: 0,
				typeDescription: "",
				code: "",
				description: "",
			},
		];
	}, [mapPosition]);

	// map API -> form mapper
	const dataNodeFormValues = (node: NodeDetailsDTO): FormValues => ({
		code: node.code ?? "",
		typeId: node.typeId ?? "",
		address: node.address ?? "",
		description: node.description ?? "",
		zipCode: node.zipCode ?? "",
		city: node.city ?? "",
		province: node.province ?? "",
		country: node.country ?? "",
		lat: node.lat ?? undefined,
		lng: node.lng ?? undefined,
		locked: node.locked ?? false,
		area: node.area?.value ?? undefined,
		capacity: node.capacity ?? undefined,
		temperature: node.temperature?.value ?? undefined,
		constructionYear: node.constructionYear ?? undefined,
		valueAddedServiceId: node.valueAddedServiceId ?? undefined,
		activityId: node.activityId ?? undefined,
		buildingCertificationId: node.buildingCertificationId ?? undefined,
		sector: "",
		computeFlag: node.computeFlag ?? 0,
	});

	// fill form in edit mode
	useEffect(() => {
		if (!isNewOperationalUnit && dataNode.data) {
			form.reset(dataNodeFormValues(dataNode.data));
		}
	}, [dataNode.data, isNewOperationalUnit, form]);

	const handleDelete = async () => {
		try {
			const ids = [id];
			await deleteNode({ ids }, { actionKey: "delete" });
			handleCloseDeleteDialog();
			visitPage(PageType.MasterDataOperationalUnits);
			navigate(AppRoutes.MasterDataOperationalUnits);
		} catch (e) {}
	};

	const nodeInformation: NodeInformation = {
		id: id,
		goodsFlowMeasureId: dataNode.data?.goodsFlowMeasureId,
		goodsFlowConversionFactor: dataNode.data?.goodsFlowConversionFactor,
	};

	const handleRedirect = () => {
		// Update breadcrumb stack
		visitPage(PageType.ResultsOperationalUnitsGreenRouterDetail, false, form.getValues("code"), id);

		// Navigate to detail route
		navigate(AppRoutes.ResultOperationalUnitsGreerouterNode.replace(":id", id));
	};

	const isSaveDisabled = !isDirty || !isValid || saveNodeLoading || updateNodeLoading;

	const selectedNodeType = form.watch("typeId");
	const generalInformationMode = LIMITED_GENERAL_NODE_TYPES.includes(Number(selectedNodeType)) ? "limited" : "full";

	const showGeneralInformation = !HIDDEN_GENERAL_NODE_TYPES.includes(Number(selectedNodeType));

	useEffect(() => {
		const forceComputeFlag =
			LIMITED_GENERAL_NODE_TYPES.includes(selectedNodeType) ||
			HIDDEN_GENERAL_NODE_TYPES.includes(selectedNodeType);

		if (forceComputeFlag && form.getValues("computeFlag") !== 1) {
			form.setValue("computeFlag", 1, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}, [selectedNodeType, form]);

	// function use to apply the node data from the gelocated query
	const handleApplyNodeLocation = (location: NodeLocationDTO) => {
		const country = countriesData?.options.find(
			(country) => String(country.id).trim().toLowerCase() === String(location.country).trim().toLowerCase()
		);

		form.setValue("lat", location.lat ?? undefined, {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("lng", location.lng ?? undefined, {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("address", location.address ?? "", {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("zipCode", location.zipCode ?? "", {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("city", location.city ?? "", {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("province", location.province ?? "", {
			shouldDirty: true,
			shouldValidate: true,
		});

		form.setValue("country", country?.id ? String(country.id) : "", {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	// error screen
	if (error) {
		return <ErrorMessage httpStatusCode={error.httpCode} />;
	}

	// actions depending on features other than the page one (creation is the page feature in "add-unit" mode)
	const isSaveForbidden = !isNewOperationalUnit && updateNodeForbidden.put;
	const isDeleteForbidden = deleteNodeForbidden.put;
	const isSearchForbidden = !canCall(ApiRoutes.ConfigurationSearchNodes, "POST");

	return (
		<View
			pageType={PageType.MasterDataOperationalUnitsDetail}
			headerContext={{
				linkToPage: !isNewOperationalUnit ? handleRedirect : undefined,
				loading: dataNode.isLoading,
			}}
		>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Stack direction="row" spacing={2}>
							{!isNewOperationalUnit && (
								<ForbiddenTooltip
									forbidden={isDeleteForbidden}
									title={t("common:content locked/action forbidden")}
								>
									<Button
										disabled={dataNode.isLoading || isDeleteForbidden}
										startIcon={<DeleteIcon />}
										onClick={handleOpenDeleteDialog}
									>
										{t("common:delete")}
									</Button>
								</ForbiddenTooltip>
							)}

							<Controller
								name="locked"
								control={form.control}
								render={({ field: { onChange, value } }) => (
									<FormControlLabel
										control={<Switch checked={value} onChange={onChange} />}
										label={
											<Typography variant="body2" color="primary" fontWeight="600">
												{t("detail/edit lock")}
											</Typography>
										}
										labelPlacement="start"
									/>
								)}
							/>
						</Stack>

						<ForbiddenTooltip
							forbidden={isSaveForbidden}
							title={t("common:content locked/action forbidden")}
						>
							<Button
								disabled={isSaveDisabled || isSaveForbidden}
								startIcon={saveNodeLoading || (updateNodeLoading && <CircularProgress size={18} />)}
								variant="contained"
								onClick={form.handleSubmit(onSubmit)}
							>
								{t("common:save")}
							</Button>
						</ForbiddenTooltip>
					</Stack>
				</Grid>

				<Grid size={showGeneralInformation ? col6 : 12}>
					<PaperCard
						header={
							<PaperCardHeader
								containerRef={containerRef}
								title={t("detail/registration details")}
								search={
									<ForbiddenTooltip
										forbidden={isSearchForbidden}
										title={t("common:content locked/action forbidden")}
									>
										<Button
											variant="outlined"
											onClick={handleOpenRegistrationDialog}
											size="small"
											startIcon={<SearchIcon />}
											disabled={locked || registrationDetailLoading || isSearchForbidden}
										>
											{t("common:search")}
										</Button>
									</ForbiddenTooltip>
								}
							/>
						}
						sxContent={{ p: 2 }}
						content={
							<RegistrationDetails
								form={form}
								openRegistrationDetailsDialog={openRegistrationDetailsDialog}
								handleClose={handleCloseRegistrationDialog}
								nodeTypes={nodeTypesData?.options ?? []}
								countries={countriesData?.options ?? []}
								loading={registrationDetailLoading}
								onApplyNodeLocation={handleApplyNodeLocation}
							/>
						}
					/>
				</Grid>

				{showGeneralInformation && (
					<Grid size={col6}>
						<PaperCard
							header={<PaperCardHeader containerRef={containerRef} title={t("detail/general data")} />}
							sxContent={{ p: 2 }}
							content={
								<GeneralInformation
									form={form}
									valueAddedServices={valueAddedServices?.options ?? []}
									computeFlags={computeFlags?.options ?? []}
									nodeActivities={nodeActivities?.options ?? []}
									certifications={certifications?.options ?? []}
									sectors={sectors?.options ?? []}
									loading={generalInformationLoading}
									isNewOperationalUnit={isNewOperationalUnit}
									nodeInformation={nodeInformation}
									mode={generalInformationMode}
								/>
							}
						/>
					</Grid>
				)}

				<Grid size={12}>
					<PaperCard
						header={<PaperCardHeader containerRef={containerRef} title={t("detail/general information")} />}
						sxContent={{ p: 2 }}
						content={
							<Box
								height={300}
								display="flex"
								justifyContent="center"
								alignItems="center"
								borderRadius={2}
								sx={{ backgroundColor: ThemeColors.LightBackground }}
							>
								{registrationDetailLoading ? (
									<Skeleton variant="rounded" height="100%" width="100%" />
								) : (
									<GraphMap
										tileProvider="heremaps"
										center={mapPosition}
										zoom={3}
										maxZoom={20}
										minZoom={1}
									>
										<MarkerLayer
											nodes={nodeData}
											color={ThemeColors.LightPrimary}
											showDetailsOnClick={false}
										/>
									</GraphMap>
								)}
							</Box>
						}
					/>
				</Grid>
			</Grid>

			<DeleteRowsDialog
				open={openDeleteDialog}
				handleClose={handleCloseDeleteDialog}
				onDelete={handleDelete}
				isLoading={deleteNodeLoading}
			/>
		</View>
	);
};
